export type LineId = 'L101' | 'L102' | 'L103' | 'L104' | string;

export type OrderStatus =
  | 'TERMINADO'
  | 'NOTIFICADO_TERMINADO'
  | 'PROGRAMADO'
  | 'LIBERADO'
  | 'INICIADO'
  | 'ESTIMADO';

export type ComponentStatus =
  | 'OK'
  | 'PENDIENTE_CALIDAD'
  | 'ENTRE_ALMACENES'
  | 'COMPRAS_EN_CURSO'
  | 'ROTURA_STOCK';

export type AcabadoType = 'SEMI' | 'RG' | 'TST';

/** Desglose oculto por componente — base del recálculo. */
/** Desglose real por componente (de tabla_planificacion) — base del recálculo. */
export interface OrderComponent {
  itemId: string;
  inventcolorid: string | null;
  inventstyleid: string | null;
  inventlocationid: string | null;
  consumoEstimado: number;
  reserva: number;
  pendiente: number;
  esGranel: boolean;             // itemId empieza por 'Q'
  /** Material bloqueado en calidad en cualquier almacén de España (del ARTÍCULO,
   *  repetido en cada fila: no se suma). null/ausente = sin dato. */
  stockCalidadSpain?: number | null;
}

/** Detalle recalculado por componente (resultado de recalcComponentStatus). */
export interface OrderComponentRecalc extends OrderComponent {
  disponibleAntes: number;    // stock libre del almacén antes de que esta OT consuma
  asignado: number;           // min(pendiente, disponibleAntes)
  disponibleDespues: number;  // disponibleAntes − asignado
  falta: number;              // pendiente − asignado
  estado: ComponentStatus;    // OK | PENDIENTE_CALIDAD | ENTRE_ALMACENES | COMPRAS_EN_CURSO | ROTURA_STOCK
  /** Cuándo llega la compra que desbloquea este componente. Sólo se rellena en
   *  COMPRAS_EN_CURSO: en el resto de estados no hay una fecha de esa
   *  naturaleza, y mezclar «llegó» con «llegará» en el mismo campo es cómo
   *  alguien acaba planificando contra algo que todavía no ha ocurrido. */
  fechaEstado?: string | null;
}

export interface ProductionOrder {
  // Identificación
  id: string;
  empresa: string;
  lineId: LineId;
  ruta: string;
  referencia: string;
  familia: string;
  subfamilia: string;
  acabado: AcabadoType;
  descripcion: string;
  esReferenciaNueva: boolean;

  // Unidades
  udOT: number;
  udProducidas: number;
  udPendientes: number;

  // Estado y fechas
  estado: OrderStatus;
  fechaPedido: string | null;
  fechaPlanificadaFin: string | null;
  /** Inicio planificado (`schedstart`). Es la fecha contra la que se juzga si
   *  una compra llega a tiempo: el material hace falta cuando se EMPIEZA a
   *  producir, no cuando se termina. Es también la que usa el Gantt, así que
   *  las dos pantallas juzgan la misma compra con el mismo listón.
   *
   *  Venía del backend desde siempre —`fecha_inicio`, entre las columnas
   *  técnicas— y el adaptador la tiraba. */
  fechaInicioPlan: string | null;
  fechaRealFin: string | null;

  // KPIs
  adherenciaPct: number | null;

  // Campos editables
  comentarios: string;
  motivoDesviacion: string;
  campoCalidad: boolean | null;

  // Logística
  granelStatus: 'OK' | 'PENDIENTE';
  granelFecha: string | null;
  componentesStatus: ComponentStatus;   // derivado por recalcComponentStatus
  componentesFecha: string | null;
  componentes: OrderComponent[];         // desglose para el recálculo
  componentesRecalc: OrderComponentRecalc[]; // detalle recalculado, paralelo a componentes

  // Productividad
  upm: number | null;
  udTurnoPromedio: number | null;
  nOperarios: number | null;

  // Posición en secuencia (mutable en memoria)
  sequencePosition: number;
  isManuallyReordered: boolean;

  // Recálculo de fechas (en memoria, esta sesión)
  isRecalculated: boolean;       // true si su fechaPlanificadaFin fue recalculada
  recalcDelta?: number;          // días naturales vs fecha original (+ adelanto, − retraso)
}

export interface LineConfig {
  lineId: LineId;
  turnos: 1 | 2 | 3;
  udTurnoPromedio: number;
  upm: number;
}

export interface CalendarDay {
  date: string;                  // ISO "2026-06-14"
  isWorkday: boolean;            // false = festivo / no laborable
  label?: string;
}

export interface RecalcResult {
  orderId: string;
  oldFechaPlanificadaFin: string | null;
  newFechaPlanificadaFin: string;
  diasNecesarios: number;        // Math.ceil de días laborables
  fechaInicio: string;           // fecha-ancla de cómputo (fin OT anterior u hoy)
}

export interface SequenceHeaderKPIs {
  totalUdComponentesOK: number;
  totalUdGranelOK: number;
  ordenEnCursoId: string | null;
}

export type SortField = keyof ProductionOrder;
export type SortDirection = 'asc' | 'desc';

export type ColumnFilterKind = 'text' | 'number' | 'set';

export interface ColumnFilter {
  field: SortField;
  kind: ColumnFilterKind;   // determina la semántica del match (no se infiere del valor)
  value: string;            // texto / "min|max" para numéricos / "a,b,c" para sets
  enabled: boolean;
}

export interface PriorityCriterio {
  field: SortField;
  label: string;       // clave i18n, no texto literal
  direction: SortDirection;
  active: boolean;
  order: number;
}

export interface PriorityConfig {
  criterios: PriorityCriterio[];
}

/** Estados cuyas filas son arrastrables / planificables. */
export const REORDERABLE_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'PROGRAMADO',
  'ESTIMADO',
  'LIBERADO',
  'INICIADO',
]);

/** Estados terminados — fijos al inicio de la secuencia. */
export const FINISHED_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'TERMINADO',
  'NOTIFICADO_TERMINADO',
]);
